import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import GridLayout, { Layout } from 'react-grid-layout';
import { fetchWidgetData } from 'components/widgets/fetchWidgetData';
import { fetchData } from 'services/api/fetchData';
import { useSettings } from 'services/settings/SettingsContext';
import { formatChartAxisDate } from 'services/utils/dateTimeUtils';
import { formatCurrencyAmount } from 'services/utils/amountFormatting';
import { CombinedWidgetDataType, DashboardLayoutItem, WidgetConfig } from 'types/WidgetTypes';
import { TimeSeriesPoint } from 'components/widgets/charts/TimeSeriesChart';
import { BreakdownDataPoint } from 'components/widgets/charts/BreakdownChart';
import { widgetCatalog, JOB_STATUS_BREAKDOWN_LABELS } from './dashboardConfig';
import BaseWidget from 'components/widgets/BaseWidget';
import MetricChartWidget from 'components/widgets/charts/MetricChartWidget';
import BreakdownChartWidget from 'components/widgets/charts/BreakdownChartWidget';
import StatusBreakdownBar, { StatusBreakdownDataPoint } from 'components/widgets/charts/StatusBreakdownBar';
import StackedBarChart, { StackedBarDataPoint } from 'components/widgets/charts/StackedBarChart';
import EmployeeBarList, { EmployeeBarDataPoint } from 'components/widgets/charts/EmployeeBarList';
import JobStatusBar from './JobStatusBar';
import FocusPanelTodos from './FocusPanelTodos';
import FocusPanelActivity from './FocusPanelActivity';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import 'react-grid-layout/css/styles.css';
import '../../style/scss/dashboard.scss';

/*
 * AdvancedWidgetsGrid.tsx
 * Renders every widget a user enabled in a react-grid-layout grid.
 * Widgets are always draggable, by grabbing anywhere on them, and
 * resizable from their bottom right corner. A drag or resize is
 * saved as soon as it is released.
 *
 * The row height is a fixed pixel value rather than derived from the
 * column width, so a number tile's content always has the same,
 * reliable amount of room regardless of screen width. Catalog height
 * values are in internal rows, 2 rows apart to allow half steps in
 * the conceptual height every widget is configured with (1, 1.5, 2,
 * ...), since react-grid-layout works best with whole row counts.
 * Two rows plus the margin between them lands on ~138px, tuned to
 * fit a number tile whose title and difference line both wrap to 2 lines.
 */

export const GRID_COLUMNS = 6;
const GRID_MARGIN = 20;
const GRID_ROW_HEIGHT = 59;

// Converts a widget's height in internal grid rows to its actual rendered pixel height
const rowsToPixels = (rows: number) => rows * GRID_ROW_HEIGHT + (rows - 1) * GRID_MARGIN;

// Space a chart widget's own padding and header take up, subtracted so the
// chart itself, not the whole card, matches the available grid height
const CHART_CHROME_HEIGHT = 60;
const MIN_CHART_HEIGHT = 80;

// Maps a statusBreakdownBar widget to the list page its segments navigate to,
// filtered on the clicked status and, if given, the current dashboard period
const STATUS_BREAKDOWN_NAVIGATION: Record<string, { path: string; dateField?: string }> = {
    proposalsByStatusValue: { path: '/proposals', dateField: 'proposal_date' },
    invoicesByStatusValue: { path: '/invoices', dateField: 'invoice_date' },
};

interface AdvancedWidgetsGridProps {
    layout: DashboardLayoutItem[];
    onLayoutChange: (layout: DashboardLayoutItem[]) => void;
    onSaveLayout: (layout: DashboardLayoutItem[]) => void;
    period: 'week' | 'month';
    periodDates: { currentPeriod: { from: string; to: string }; previousPeriod: { from: string; to: string } };
    handleLogout: (options?: { logoutFromServer?: boolean, showSessionAlert?: boolean }) => Promise<void>;
    extraParams?: Record<string, string>;
}

const AdvancedWidgetsGrid: React.FC<AdvancedWidgetsGridProps> = ({
    layout, onLayoutChange, onSaveLayout, period, periodDates, handleLogout, extraParams,
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const history = useHistory();
    const { currentPeriod } = periodDates;

    // Navigate to the list page a statusBreakdownBar widget is configured for,
    // filtered on the clicked status and the current dashboard period
    const navigateToStatusBreakdownGroup = (widgetKey: string, group: string) => {
        const target = STATUS_BREAKDOWN_NAVIGATION[widgetKey];
        if (!target) {
            return;
        }

        const params = new URLSearchParams({ status: group });
        if (target.dateField) {
            params.set(`${target.dateField}__gte`, currentPeriod.from);
            params.set(`${target.dateField}__lte`, currentPeriod.to);
        }

        history.push(`${target.path}?${params.toString()}`);
    };
    const extraParamsKey = extraParams ? JSON.stringify(extraParams) : '';

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [gridRevealed, setGridRevealed] = useState<boolean>(false);
    const hasScheduledReveal = useRef<boolean>(false);

    const [widgetData, setWidgetData] = useState<CombinedWidgetDataType>({});
    const [tilesLoading, setTilesLoading] = useState<boolean>(true);
    const [chartData, setChartData] = useState<{ [widgetKey: string]: TimeSeriesPoint[] | BreakdownDataPoint[] | StatusBreakdownDataPoint[] | StackedBarDataPoint[] | EmployeeBarDataPoint[] }>({});
    const [chartsLoading, setChartsLoading] = useState<boolean>(true);

    // Remove a widget from the layout and save it
    const handleRemoveWidget = (widgetKey: string) => {
        onSaveLayout(layout.filter(item => item.widget_key !== widgetKey));
    };

    // Measure the container so the row height can be derived from the column width.
    // Take an immediate synchronous measurement so the grid renders on the first
    // paint, the observer only needs to catch up on later size changes
    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        setContainerWidth(containerRef.current.getBoundingClientRect().width);

        const observer = new ResizeObserver(entries => {
            setContainerWidth(entries[0].contentRect.width);
        });
        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, []);

    // React-grid-layout animates every position it is given, including its very
    // first one, which shows as an unwanted slide-in from the top left the moment
    // the grid mounts. Keep the grid invisible until that initial transition has
    // had time to settle, then reveal the whole, already positioned grid at once
    useEffect(() => {
        if (containerWidth > 0 && !hasScheduledReveal.current) {
            hasScheduledReveal.current = true;
            const timeout = setTimeout(() => setGridRevealed(true), 300);
            return () => clearTimeout(timeout);
        }
    }, [containerWidth]);

    // Only include catalog widgets which are actually present in the saved layout
    const activeWidgets = widgetCatalog.filter(widget => layout.some(item => item.widget_key === widget.widgetKey));
    const numberWidgets = activeWidgets.filter(widget => widget.widgetType === 'number');
    const chartWidgets = activeWidgets.filter(widget => !!widget.endpoint);

    // Fetch the number tile widget data
    useEffect(() => {
        const fetchTiles = async () => {
            if (numberWidgets.length === 0) {
                setWidgetData({});
                setTilesLoading(false);
                return;
            }

            // Build a widget config per number tile, matching the shape fetchWidgetData expects
            const numberWidgetConfigs: WidgetConfig[] = numberWidgets.map(widget => ({
                widgetKey: widget.widgetKey,
                variant: widget.variant || 'numberWidget',
                title: widget.title,
                query: widget.query!,
                calculationMethod: widget.calculationMethod || 'divide',
            }));

            const data = await fetchWidgetData(numberWidgetConfigs, periodDates, handleLogout, extraParams);
            setWidgetData(data);
            setTilesLoading(false);
        };

        setTilesLoading(true);
        fetchTiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layout.map(item => item.widget_key).join(','), period, extraParamsKey]);

    // Fetch the chart, breakdown and job status bar widget data
    useEffect(() => {
        const fetchCharts = async () => {
            const newChartData: { [widgetKey: string]: TimeSeriesPoint[] | BreakdownDataPoint[] } = {};
            const extraParamsString = extraParams
                ? Object.entries(extraParams).map(([key, value]) => `&${key}=${value}`).join('')
                : '';

            await Promise.all(chartWidgets.map(async widget => {
                const endpoint = `${widget.endpoint}/?from=${currentPeriod.from}&to=${currentPeriod.to}${extraParamsString}`;
                const response = await fetchData({ apiUrl: endpoint, handleLogout });
                newChartData[widget.widgetKey] = response || [];
            }));

            setChartData(newChartData);
            setChartsLoading(false);
        };

        setChartsLoading(true);
        fetchCharts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layout.map(item => item.widget_key).join(','), period, extraParamsKey]);

    // Build the grid layout array, merging the saved position with the catalog size limits
    const buildGridLayout = (currentLayout: DashboardLayoutItem[]): Layout[] => currentLayout
        .filter(item => activeWidgets.some(widget => widget.widgetKey === item.widget_key))
        .map(item => {
            const widget = activeWidgets.find(widget => widget.widgetKey === item.widget_key)!;
            return {
                i: item.widget_key,
                x: item.x,
                y: item.y,
                w: item.w,
                h: item.h,
                minW: widget.minGridSize.w,
                maxW: widget.maxGridSize.w,
                minH: widget.minGridSize.h,
                maxH: widget.maxGridSize.h,
            };
        });

    const gridLayout = buildGridLayout(layout);

    // Translate a react-grid-layout layout array back into the saved layout shape
    const toDashboardLayout = (newGridLayout: Layout[]): DashboardLayoutItem[] => newGridLayout.map(item => ({
        widget_key: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
    }));

    // Update the live layout while dragging or resizing, without saving on every intermediate step
    const handleGridLayoutChange = (newGridLayout: Layout[]) => {
        const newLayout = toDashboardLayout(newGridLayout);

        // Only propagate the change if a position or size actually differs, react-grid-layout
        // fires this callback on every render otherwise and would cause an update loop
        const isUnchanged = newLayout.length === layout.length && newLayout.every(newItem => {
            const currentItem = layout.find(item => item.widget_key === newItem.widget_key);
            return currentItem && currentItem.x === newItem.x && currentItem.y === newItem.y
                && currentItem.w === newItem.w && currentItem.h === newItem.h;
        });

        if (!isUnchanged) {
            onLayoutChange(newLayout);
        }
    };

    // Save the layout once a drag or resize is released
    const handleInteractionStop = (newGridLayout: Layout[]) => {
        onSaveLayout(toDashboardLayout(newGridLayout));
    };

    // Look up a widget's actual rendered pixel height from its saved layout row count
    const getWidgetPixelHeight = (widgetKey: string) => {
        const item = layout.find(item => item.widget_key === widgetKey);
        return item ? rowsToPixels(item.h) : rowsToPixels(2);
    };

    // The chart itself gets the widget's height minus its own padding and header
    const getChartHeight = (widgetKey: string) => Math.max(getWidgetPixelHeight(widgetKey) - CHART_CHROME_HEIGHT, MIN_CHART_HEIGHT);

    // A donut's legend sits to the side once the widget is wider than a single column, below it otherwise
    const getDonutLegendPosition = (widgetKey: string): 'bottom' | 'side' => {
        const item = layout.find(item => item.widget_key === widgetKey);
        return item && item.w > 1 ? 'side' : 'bottom';
    };

    return (
        <div ref={containerRef} style={{ visibility: gridRevealed ? 'visible' : 'hidden' }}>
            {activeWidgets.length > 0 && containerWidth > 0 && (
                <GridLayout
                    className='advanced-widgets-grid'
                    layout={gridLayout}
                    width={containerWidth}
                    cols={GRID_COLUMNS}
                    rowHeight={GRID_ROW_HEIGHT}
                    margin={[GRID_MARGIN, GRID_MARGIN]}
                    isDraggable
                    isResizable
                    draggableCancel='.no-drag'
                    onLayoutChange={handleGridLayoutChange}
                    onDragStop={handleInteractionStop}
                    onResizeStop={handleInteractionStop}
                >
                    {activeWidgets.map(widget => (
                        <div key={widget.widgetKey} className='advanced-widget-item'>
                            <span
                                className='widget-remove-icon no-drag'
                                onClick={() => handleRemoveWidget(widget.widgetKey)}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </span>
                            {widget.widgetType === 'number' ? (
                                <BaseWidget
                                    widgetKey={widget.widgetKey}
                                    variant={widget.variant || 'numberWidget'}
                                    title={widget.title}
                                    tooltipText={widget.tooltipText}
                                    period={period}
                                    query={widget.query!}
                                    data={widgetData[widget.widgetKey] || { defaultData: { current: [], previous: [] } }}
                                    loading={tilesLoading}
                                    calculationMethod={widget.calculationMethod || 'divide'}
                                    prefix={widget.prefix}
                                    measuringUnit={widget.measuringUnit}
                                    decimals={widget.decimals}
                                    moreIsNegative={widget.moreIsNegative}
                                    hideComparison={widget.hideComparison}
                                    displayAsPercentage={widget.displayAsPercentage}
                                    showComparison={widget.showComparison}
                                />
                            ) : widget.widgetType === 'timeSeries' ? (
                                <MetricChartWidget
                                    title={t(widget.title)}
                                    titleClassName='title'
                                    data={(chartData[widget.widgetKey] as TimeSeriesPoint[]) || []}
                                    series={(widget.series || []).map(series => ({ ...series, label: t(series.label) }))}
                                    loading={chartsLoading}
                                    height={getChartHeight(widget.widgetKey)}
                                    xFormatter={(value) => formatChartAxisDate(value, userLocale)}
                                    allowDecimals={widget.allowDecimals}
                                />
                            ) : widget.widgetType === 'breakdown' ? (
                                <BreakdownChartWidget
                                    title={t(widget.title)}
                                    titleClassName='title'
                                    tooltipText={widget.tooltipText ? t(widget.tooltipText) : undefined}
                                    data={(chartData[widget.widgetKey] as BreakdownDataPoint[]) || []}
                                    groupLabels={Object.fromEntries(
                                        Object.entries(widget.groupLabelKeys || {}).map(([key, label]) => [key, t(label)])
                                    )}
                                    loading={chartsLoading}
                                    height={getChartHeight(widget.widgetKey)}
                                    variant={widget.chartVariant}
                                    yFormatter={widget.valueFormat === 'currency' ? (value) => formatCurrencyAmount(value, userLocale) : undefined}
                                    legendPosition={getDonutLegendPosition(widget.widgetKey)}
                                />
                            ) : widget.widgetType === 'jobStatusBar' ? (
                                <JobStatusBar
                                    data={(chartData[widget.widgetKey] as BreakdownDataPoint[]) || []}
                                    groupLabels={Object.fromEntries(
                                        Object.entries(JOB_STATUS_BREAKDOWN_LABELS).map(([key, label]) => [key, t(label)])
                                    )}
                                    loading={chartsLoading}
                                    periodFrom={currentPeriod.from}
                                    periodTo={currentPeriod.to}
                                />
                            ) : widget.widgetType === 'statusBreakdownBar' ? (
                                <StatusBreakdownBar
                                    title={t(widget.title)}
                                    titleClassName='title'
                                    tooltipText={widget.tooltipText ? t(widget.tooltipText) : undefined}
                                    data={(chartData[widget.widgetKey] as StatusBreakdownDataPoint[]) || []}
                                    groupLabels={Object.fromEntries(
                                        Object.entries(widget.groupLabelKeys || {}).map(([key, label]) => [key, t(label)])
                                    )}
                                    groupColors={widget.groupColors}
                                    loading={chartsLoading}
                                    valueField={widget.barValueField}
                                    valueFormatter={
                                        widget.valueFormat === 'currency' ? (value) => formatCurrencyAmount(value, userLocale) :
                                            widget.valueFormat === 'hours' ? (value) => `${value.toFixed(1)}u` : undefined
                                    }
                                    onSegmentClick={STATUS_BREAKDOWN_NAVIGATION[widget.widgetKey] ? (group) => navigateToStatusBreakdownGroup(widget.widgetKey, group) : undefined}
                                />
                            ) : widget.widgetType === 'stackedBar' ? (
                                <StackedBarChart
                                    title={t(widget.title)}
                                    titleClassName='title'
                                    tooltipText={widget.tooltipText ? t(widget.tooltipText) : undefined}
                                    data={(chartData[widget.widgetKey] as StackedBarDataPoint[]) || []}
                                    series={(widget.series || []).map(series => ({ ...series, label: t(series.label) }))}
                                    loading={chartsLoading}
                                    height={getChartHeight(widget.widgetKey)}
                                    yFormatter={widget.stackedBarUnit ? (value) => `${value}${widget.stackedBarUnit}` : undefined}
                                    stacked={widget.stackedBarStacked}
                                />
                            ) : widget.widgetType === 'employeeBar' ? (
                                <EmployeeBarList
                                    title={t(widget.title)}
                                    titleClassName='title'
                                    tooltipText={widget.tooltipText ? t(widget.tooltipText) : undefined}
                                    data={(chartData[widget.widgetKey] as EmployeeBarDataPoint[]) || []}
                                    series={(widget.series || []).map(series => ({ ...series, label: t(series.label) }))}
                                    loading={chartsLoading}
                                    valueFormatter={widget.stackedBarUnit ? (value) => `${value}${widget.stackedBarUnit}` : undefined}
                                />
                            ) : widget.widgetType === 'focusPanelTodos' ? (
                                <FocusPanelTodos />
                            ) : (
                                <FocusPanelActivity />
                            )}
                        </div>
                    ))}
                </GridLayout>
            )}
        </div>
    );
};

export default AdvancedWidgetsGrid;
